import React, { ReactNode, useMemo } from "react";
import classNames from "classnames";

import "./progress-loader.scss";

export interface ProgressLoaderProps {
  className?: string;
  show?: boolean;
  fullscreen?: boolean;
  children?: ReactNode;
  tip?: ReactNode;
  variant?: "default" | "linear" | "circular";
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const ProgressLoader: React.FC<
  ProgressLoaderProps & { [x: string]: any }
> = ({
  className = "",
  fullscreen = false,
  children = null,
  show = false,
  tip = null,
  variant = "default",
  size = "md",
  ...other
}) => {
  const classes = classNames({
    "motif-progress-loader": true,
    "motif-progress-loader-fullscreen": fullscreen,
    "motif-progress-loading": children && show,
    [className]: className ? true : false
  });

  const childrenClasses = classNames({
    "motif-progress-nested": true,
    "motif-progress-blur": children && show
  });

  const circularLoaderClasses = classNames({
    "motif-progress-loader-circular": true,
    [`motif-progress-loader-circular-${size}`]: size
  });

  const loaderType = useMemo(() => {
    switch (variant) {
      case "circular":
        return <span className={circularLoaderClasses}></span>;
      case "linear":
        return <span className="motif-progress-loader-linear"></span>;
      case "default":
      default:
        return (
          <div className="motif-progress-loader-content">
            <div className="motif-progress-loader-square" />
            <div className="motif-progress-loader-square" />
            <div className="motif-progress-loader-square" />
          </div>
        );
    }
  }, [variant, size]);

  const getContent = () => {
    const loaderContent = (
      <div className="motif-progress-loader-type">
        {loaderType}
        {tip && <div>{tip}</div>}
      </div>
    );

    if (!show) {
      return <div className={childrenClasses}>{children}</div>;
    } else {
      return (
        <>
          {loaderContent}
          {children && <div className={childrenClasses}>{children}</div>}
        </>
      );
    }
  };

  return (
    <React.Fragment>
      <div {...other} className={classes} data-motif-progress-loader>
        {getContent()}
      </div>
    </React.Fragment>
  );
};
